import {
  isPlaybookSectionNode,
  type PlaybookNode,
} from "@repo/app-types/entities/playbook";
import type { FC, ReactNode } from "react";

export interface FeedbackReviewNodePanelProps {
  /** Published node content used to grade the feedback. */
  node: PlaybookNode | undefined;
  /** Draft node that determines whether and where this target is navigable. */
  draftNode: PlaybookNode | undefined;
  /**
   * The node's place in the review queue ("Criterion 2 of 5 open"); null when
   * it has no open feedback.
   */
  openFeedbackPosition: { position: number; total: number } | null;
}

/**
 * The review tab's left panel: the published instructions the grader used for
 * the selected node. Read-only on purpose — draft structure decides which node
 * is selected and where it appears, while the Playbook tab owns editing.
 */
export const FeedbackReviewNodePanel: FC<FeedbackReviewNodePanelProps> = (
  props,
) => {
  const { node, draftNode, openFeedbackPosition } = props;

  if (draftNode && node) {
    const isSection = isPlaybookSectionNode(node);

    return (
      <div className="flex min-h-0 flex-col overflow-hidden bg-white px-4 pb-4">
        <div className="shrink-0 pb-4">
          <p className="flex min-h-7 items-center justify-between gap-2">
            <span className="text-sm font-medium text-foreground">
              {isSection ? "Section" : "Criteria"}
            </span>
            {openFeedbackPosition ? (
              <span className="text-xs text-muted-foreground">
                {openFeedbackPosition.position} of {openFeedbackPosition.total}{" "}
                open
              </span>
            ) : null}
          </p>
          <h2 className="mt-1 text-base font-semibold text-foreground">
            {node.name}
          </h2>
        </div>

        <div className="min-h-0 overflow-y-auto">
          <p className="mb-1 text-xs font-medium text-muted-foreground">
            {isSection ? "Description" : "Grading context"}
          </p>
          <NodeText
            value={isSection ? node.description : node.grading_context}
            emptyMessage={
              isSection
                ? "No description for this section."
                : "No grading context provided."
            }
          />
        </div>
      </div>
    );
  }

  const message = draftNode
    ? "This node has not been published yet, so there is no live grading reference."
    : "This playbook has no criteria yet.";

  return (
    <div className="flex min-h-0 flex-col bg-white p-4">
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
};

interface NodeTextProps {
  value: string | undefined;
  emptyMessage: ReactNode;
}

const NodeText: FC<NodeTextProps> = (props) => {
  const { value, emptyMessage } = props;

  if (!value?.trim()) {
    return (
      <p className="text-sm text-muted-foreground italic">{emptyMessage}</p>
    );
  }

  return <p className="text-sm whitespace-pre-wrap text-foreground">{value}</p>;
};
